/*
 * ErrorBoundary — the SPA had none, so a single render-time exception anywhere
 * unmounted the whole tree and left a blank page. That is worst in the two
 * full-screen editors, where a blank page also throws away the design the user
 * was working on with no hint of what happened.
 *
 * Real case this was written for: a numeric Ring Builder slider received a
 * non-numeric value over RPC, `display()` called `.toFixed()` on it, and the
 * entire /ring route went white. Reloading recovered the app but lost the work.
 *
 * React only surfaces render/lifecycle errors here. Async rejections (RPC
 * failures, fetches) still need their own try/catch — those already toast.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Shown above the message, e.g. "Ring Builder". */
  label?: string;
  /** Rendered instead of the default panel when provided. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Keep the component stack in the console — without it the only signal a
    // developer got from this class of failure was a blank page.
    console.error(`[ErrorBoundary${this.props.label ? ` · ${this.props.label}` : ''}]`, error, info.componentStack);
  }

  private reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div
        role="alert"
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 12, height: '100%', minHeight: 260, padding: 24, textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 600 }}>
          {this.props.label ? `${this.props.label} hit an error` : 'Something went wrong'}
        </div>
        <div style={{ fontSize: 13, opacity: 0.7, maxWidth: 460 }}>
          The view stopped rendering. Try again — if it keeps happening, reload the page.
        </div>
        <code style={{ fontSize: 11, opacity: 0.55, maxWidth: 560, overflowWrap: 'anywhere' }}>
          {error.message}
        </code>
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button className="btn" onClick={this.reset}>Try again</button>
          <button className="btn ghost" onClick={() => window.location.reload()}>Reload</button>
        </div>
      </div>
    );
  }
}
